/**
 * Currency Utilities
 * Handles currency symbol display and formatting for multiple currencies
 */

/**
 * Get currency symbol from currency code
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'usd', 'eur', 'gbp')
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  if (!currencyCode) return '$'; // Default to USD
  
  // Convert to uppercase to handle both lowercase and uppercase codes
  const code = currencyCode.toUpperCase();
  
  const currencySymbols = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'CAD': 'C$',
    'AUD': 'A$',
    'CHF': 'CHF',
    'CNY': '¥',
    'ILS': '₪',
    'INR': '₹',
    'KRW': '₩',
    'MXN': 'MX$',
    'NZD': 'NZ$',
    'SEK': 'kr',
    'SGD': 'S$',
    'HKD': 'HK$',
    'NOK': 'kr',
    'DKK': 'kr',
    'PLN': 'zł',
    'BRL': 'R$',
    'THB': '฿',
    'ZAR': 'R',
    'RUB': '₽',
    'TRY': '₺',
    'AED': 'AED',
    'SAR': 'SR',
    'MAD': 'MAD',
    'EGP': 'E£',
    'MYR': 'RM',
    'PHP': '₱',
    'IDR': 'Rp'
  };
  
  return currencySymbols[code] || code;
};

/**
 * Format amount with currency symbol
 * @param {number} amount - Amount in cents
 * @param {string} currencyCode - ISO 4217 currency code (e.g., 'usd', 'eur', 'gbp')
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'usd') => {
  if (amount == null || isNaN(amount)) return 'N/A';
  
  const symbol = getCurrencySymbol(currencyCode);
  const dollars = (amount / 100).toFixed(2);
  
  // Format with thousands separator
  const formattedAmount = parseFloat(dollars).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
  
  return `${symbol}${formattedAmount}`;
};

/**
 * Format currency for display with proper locale formatting
 * @param {number} amountCents - Amount in cents
 * @param {string} currencyCode - ISO 4217 currency code
 * @param {string} locale - BCP 47 language tag (default: 'en-US')
 * @returns {string} Formatted amount with currency
 */
export const formatCurrencyLocale = (amountCents, currencyCode = 'usd', locale = 'en-US') => {
  const amount = amountCents / 100;
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  } catch (error) {
    // Fallback to simple formatting if Intl fails
    console.warn(`Failed to format currency ${currencyCode}, using fallback:`, error);
    return formatCurrency(amountCents, currencyCode);
  }
};

/**
 * Get currency name from code
 * @param {string} currencyCode - ISO 4217 currency code
 * @returns {string} Currency name
 */
export const getCurrencyName = (currencyCode) => {
  if (!currencyCode) return 'US Dollar';
  
  const code = currencyCode.toUpperCase();
  
  const currencyNames = {
    'USD': 'US Dollar',
    'EUR': 'Euro',
    'GBP': 'British Pound',
    'JPY': 'Japanese Yen',
    'CAD': 'Canadian Dollar',
    'AUD': 'Australian Dollar',
    'CHF': 'Swiss Franc',
    'CNY': 'Chinese Yuan',
    'ILS': 'Israeli Shekel',
    'INR': 'Indian Rupee',
    'KRW': 'South Korean Won',
    'MXN': 'Mexican Peso',
    'NZD': 'New Zealand Dollar',
    'SEK': 'Swedish Krona',
    'SGD': 'Singapore Dollar',
    'HKD': 'Hong Kong Dollar',
    'NOK': 'Norwegian Krone',
    'DKK': 'Danish Krone',
    'PLN': 'Polish Złoty',
    'BRL': 'Brazilian Real',
    'THB': 'Thai Baht',
    'ZAR': 'South African Rand',
    'RUB': 'Russian Ruble',
    'TRY': 'Turkish Lira',
    'AED': 'UAE Dirham',
    'SAR': 'Saudi Riyal',
    'MAD': 'Moroccan Dirham',
    'EGP': 'Egyptian Pound',
    'MYR': 'Malaysian Ringgit',
    'PHP': 'Philippine Peso',
    'IDR': 'Indonesian Rupiah'
  };
  
  return currencyNames[code] || code;
};

export default {
  getCurrencySymbol,
  formatCurrency,
  formatCurrencyLocale,
  getCurrencyName
};
